// App shell: MainMenu → FactionSelect → Match (+ DeckEditor / Settings / Profile),
// with a full-screen pause overlay inside the match (Match never unmounts mid-game).

import { useEffect, useRef, useState } from 'react';
import { Game, randomMatchSeed } from '../app/game';
import { AudioDirector } from '../app/audio';
import {
  loadSettings,
  recordResult,
  type SettingsData,
} from '../app/persistence';
import { FACTIONS, type MatchSetup } from '../content/factions';
import { validateContent } from '../content/registry';
import {
  Alerts,
  BossSummonCountdown,
  BoxSelect,
  CardBar,
  CheatPanel,
  CommandBar,
  CountdownBanner,
  DenyToast,
  Minimap,
  PendingHint,
  ResultsOverlay,
  SelectionPanel,
  TopBar,
  WorkerPanel,
} from './hud';
import { PauseMenu } from './PauseMenu';
import { DeckEditor } from './screens/DeckEditor';
import { FactionSelect } from './screens/FactionSelect';
import { MainMenu } from './screens/MainMenu';
import { OnlineLobby, type OnlineMatchInfo } from './screens/OnlineLobby';
import { ProfileScreen } from './screens/Profile';
import { SettingsScreen } from './screens/Settings';
import { ModelViewer } from './screens/ModelViewer';
import { PlayModeSelect } from './screens/PlayModeSelect';
import { TutorialOverlay } from './TutorialOverlay';

type Screen = 'menu' | 'playMode' | 'factionSelect' | 'online' | 'deckEditor' | 'settings' | 'profile' | 'modelViewer' | 'match';

export function App() {
  const rootRef = useRef<HTMLDivElement>(null);
  const [audio, setAudio] = useState<AudioDirector | null>(null);
  const [screen, setScreen] = useState<Screen>('menu');
  const [matchKey, setMatchKey] = useState(0);
  const [matchSetup, setMatchSetup] = useState<MatchSetup | null>(null);
  const [onlineMatch, setOnlineMatch] = useState<OnlineMatchInfo | null>(null);
  const [contentProblems] = useState<string[]>(() => validateContent());
  // Bumped whenever Settings applies, so the body-level motion class re-syncs immediately.
  const [settingsRevision, setSettingsRevision] = useState(0);

  // Reduced Motion has to reach <body>: the shared transitions and screen animations are built
  // on the --dur-* tokens in tokens.css, and the existing .reduce-motion class only ever landed
  // on .hudroot and .deckforge, so menus kept animating with the setting on.
  useEffect(() => {
    document.body.classList.toggle('reduce-motion', loadSettings().reduceMotion);
  }, [settingsRevision, screen]);

  useEffect(() => {
    // Audio owns DOM listeners and an AudioContext, so create it in an effect rather than a
    // state initializer (React StrictMode intentionally evaluates initializers twice in dev).
    const director = new AudioDirector(loadSettings());
    setAudio(director);
    return () => director.dispose();
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !audio) return;
    const interactiveFor = (target: EventTarget | null): HTMLElement | null => {
      if (!(target instanceof Element)) return null;
      const element = target.closest<HTMLElement>(
        '[data-audio], button:not(:disabled), input:not(:disabled), select:not(:disabled), [role="button"]',
      );
      return element && root.contains(element) ? element : null;
    };
    const hover = (event: PointerEvent | FocusEvent) => {
      const element = interactiveFor(event.target);
      if (!element) return;
      const previous = event.relatedTarget;
      if (previous instanceof Node && element.contains(previous)) return;
      audio.ui(element.dataset.audio === 'card' ? 'cardHover' : 'hover');
    };
    const click = (event: MouseEvent) => {
      const element = interactiveFor(event.target);
      if (!element) return;
      audio.ui(element.dataset.audio === 'card' ? 'cardPick' : 'click');
    };
    root.addEventListener('pointerover', hover);
    root.addEventListener('focusin', hover);
    root.addEventListener('click', click);
    return () => {
      root.removeEventListener('pointerover', hover);
      root.removeEventListener('focusin', hover);
      root.removeEventListener('click', click);
    };
  }, [audio]);

  useEffect(() => {
    audio?.setMusicScene(screen === 'match' ? 'match' : 'menu');
  }, [audio, screen]);

  const startMatch = (setup: MatchSetup) => {
    setMatchSetup(setup);
    setMatchKey((k) => k + 1);
    setScreen('match');
  };

  return (
    <div className="app" ref={rootRef}>
      {screen === 'menu' && (
        <MainMenu
          problems={contentProblems}
          onPlay={() => setScreen('playMode')}
          onTutorial={() => {
            const faction = 'iron-covenant' as const;
            startMatch({
              mode: '1v1',
              mapId: 'crownline-crossing',
              tutorial: true,
              players: [
                { faction, deck: [...FACTIONS[faction].defaultDeck], isAI: false, team: 0 },
                { faction, deck: [...FACTIONS[faction].defaultDeck], isAI: true, aiDifficulty: 'easy', team: 1 },
              ],
            });
          }}
          onDeckEditor={() => setScreen('deckEditor')}
          onProfile={() => setScreen('profile')}
          onSettings={() => setScreen('settings')}
        />
      )}
      {screen === 'playMode' && (
        <PlayModeSelect
          onSinglePlayer={() => setScreen('factionSelect')}
          onOnline={() => setScreen('online')}
          onBack={() => setScreen('menu')}
        />
      )}
      {screen === 'factionSelect' && (
        <FactionSelect onStart={startMatch} onBack={() => setScreen('playMode')} />
      )}
      {screen === 'online' && (
        <OnlineLobby
          onStart={(info) => {
            setOnlineMatch(info);
            setMatchSetup(info.setup);
            setMatchKey((k) => k + 1);
            setScreen('match');
          }}
          onBack={() => setScreen('playMode')}
        />
      )}
      {screen === 'deckEditor' && <DeckEditor onBack={() => setScreen('menu')} />}
      {screen === 'settings' && (
        <SettingsScreen
          onBack={() => setScreen('menu')}
          onChanged={(settings) => { audio?.setSettings(settings); setSettingsRevision((value) => value + 1); }}
          onOpenModelViewer={() => setScreen('modelViewer')}
        />
      )}
      {screen === 'profile' && <ProfileScreen onBack={() => setScreen('menu')} />}
      {screen === 'modelViewer' && <ModelViewer onBack={() => setScreen('settings')} />}
      {screen === 'match' && matchSetup && audio && (
        <Match
          key={matchKey}
          setup={matchSetup}
          seed={onlineMatch?.seed}
          onRestart={() => {
            if (onlineMatch) {
              // The server has no rematch flow — remounting the match would wait
              // forever on ticks from a finished server match. Return to the lobby.
              onlineMatch.client.close();
              setOnlineMatch(null);
              setScreen('online');
            } else {
              setMatchKey((k) => k + 1);
            }
          }}
          onMenu={() => {
            onlineMatch?.client.close();
            setOnlineMatch(null);
            setScreen('menu');
          }}
          online={onlineMatch ?? undefined}
          tutorial={Boolean(matchSetup.tutorial)}
          audio={audio}
        />
      )}
    </div>
  );
}

function Match({
  setup,
  seed,
  onRestart,
  onMenu,
  online,
  tutorial,
  audio,
}: {
  setup: MatchSetup;
  seed?: number;
  onRestart: () => void;
  onMenu: () => void;
  online?: OnlineMatchInfo;
  tutorial: boolean;
  audio: AudioDirector;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [game, setGame] = useState<Game | null>(null);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [hudScale, setHudScale] = useState(() => loadSettings().hudScale);
  const [, setPulse] = useState(0);
  const recordedRef = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const g = new Game(el, seed ?? randomMatchSeed(), setup, loadSettings(), online ? {
      client: online.client,
      matchId: online.matchId,
      localPlayer: online.localPlayer,
    } : undefined, audio);
    setGame(g);
    // HUD refresh at 10 Hz (plan §31.2)
    const iv = window.setInterval(() => setPulse((p) => p + 1), 100);
    return () => {
      window.clearInterval(iv);
      g.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audio, online, seed, setup]);

  // record the result exactly once when the match ends (GDD §42.1)
  useEffect(() => {
    if (!game || recordedRef.current) return;
    const iv = window.setInterval(() => {
      const st = game.sim.state;
      if (st.phase === 'ended' && !recordedRef.current) {
        recordedRef.current = true;
        const localTeam = st.players[game.localPlayer].team;
        recordResult({
          at: Date.now(),
          mode: setup.mode,
          mapId: setup.mapId,
          playerFaction: setup.players[game.localPlayer]!.faction,
          allyFactions: setup.players.filter((_, id) => id !== game.localPlayer && st.players[id]?.team === localTeam).map((p) => p.faction),
          enemyFactions: setup.players.filter((_, id) => st.players[id]?.team !== localTeam).map((p) => p.faction),
          result: st.winningTeam === -1 ? 'abandoned' : st.winningTeam === localTeam ? 'victory' : 'defeat',
          source: online ? 'online' : 'single',
          durationSec: st.time,
        });
      }
    }, 500);
    return () => window.clearInterval(iv);
  }, [game, online, setup]);

  const openPause = () => {
    if (!game) return;
    if (!game.onlineRuntime) game.sim.state.paused = true;
    setPauseOpen(true);
  };
  const closePause = () => {
    if (game && !game.onlineRuntime) game.sim.state.paused = false;
    setPauseOpen(false);
  };
  const concede = () => {
    if (game && !recordedRef.current && game.sim.state.phase !== 'ended') {
      recordedRef.current = true;
      game.concede();
      const localTeam = game.sim.state.players[game.localPlayer].team;
      recordResult({
        at: Date.now(),
        mode: setup.mode,
        mapId: setup.mapId,
        playerFaction: setup.players[game.localPlayer]!.faction,
        allyFactions: setup.players.filter((_, id) => id !== game.localPlayer && game.sim.state.players[id]?.team === localTeam).map((p) => p.faction),
        enemyFactions: setup.players.filter((_, id) => game.sim.state.players[id]?.team !== localTeam).map((p) => p.faction),
        result: 'defeat',
        source: online ? 'online' : 'single',
        durationSec: game.sim.state.time,
      });
    }
    onMenu();
  };
  const applySettings = (s: SettingsData) => {
    setHudScale(s.hudScale);
    if (game) game.applySettings(s);
  };

  return (
    <>
      <div className="viewport" ref={containerRef} />
      {game && (
        <div
          className={`hudroot${game.settings.reduceMotion ? ' reduce-motion' : ''}${game.settings.largeCardText ? ' large-card-text' : ''}`}
          data-foil={game.settings.foilEffects ? 'on' : 'off'}
          style={{
            zoom: hudScale,
            // CSS zoom also enlarges an absolutely-positioned 100% box. Give the HUD
            // the inverse layout size so its scaled edge still lands on the viewport
            // edge instead of clipping the bottom/right controls in windowed play.
            width: `${100 / hudScale}%`,
            height: `${100 / hudScale}%`,
          }}
        >
          <TopBar game={game} onOpenMenu={openPause} />
          <Alerts game={game} />
          {game.settings.cheats && !game.onlineRuntime && <CheatPanel game={game} />}
          <WorkerPanel game={game} />
          <Minimap game={game} />
          <CardBar game={game} />
          <CommandBar game={game} />
          <SelectionPanel game={game} />
          <PendingHint game={game} />
          <DenyToast game={game} />
          <BoxSelect game={game} />
          <CountdownBanner game={game} />
          <BossSummonCountdown game={game} />
          <ResultsOverlay game={game} onRestart={onRestart} onMenu={onMenu} />
          {tutorial && <TutorialOverlay game={game} onExit={onMenu} />}
          <PauseMenu
            open={pauseOpen && game.sim.state.phase !== 'ended'}
            onResume={closePause}
            onRestart={onRestart}
            onConcede={concede}
            onSettingsChanged={applySettings}
            online={Boolean(game.onlineRuntime)}
          />
        </div>
      )}
    </>
  );
}
